import { neverVisible, sampleTextVisibility } from './readingTimeRule';
import type { LeafVisibility } from './readingTimeRule';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

/**
 * Copy the voice hasn't reached yet is read before it is heard, and a viewer reading one thing while
 * listening to another takes in neither — the stat that ticks up two words before it is said, the
 * label that sits waiting for the sentence to catch up. A reveal a moment ahead of its word reads as
 * anticipation rather than as a spoiler, so the bar leaves that much room.
 */
const LEAD_TOLERANCE_SECONDS = 0.3;

/**
 * How long after its spoken onset a word revealed on its own may first be legible. Wide enough for
 * the pose grid — a word is first seen up to one ~0.33s sample after it became legible — plus a short
 * fade; a word later than that has the line reading behind the voice.
 */
const LAG_TOLERANCE_SECONDS = 0.5;

/**
 * A line counts as revealed word by word only with at least this many words of its own. Below it, a
 * word styled as its own span is emphasis inside a sentence, not a reveal timed word against word.
 */
const MIN_REVEALED_WORDS = 3;

/**
 * Copy counts as the narration's own words only when more than this share of its distinct words is
 * spoken in the scene. A line that merely shares an "it" or a "this" with the voiceover is the
 * design's own copy, whatever the language, and its timing is not the narration's to set.
 */
const SPOKEN_SHARE = 0.5;

/** Word boundaries by the Unicode rules, so a script written without spaces splits into words too. */
const WORDS = new Intl.Segmenter(undefined, { granularity: 'word' });

/**
 * The words of `text` as comparable keys: a figure by its digits, so "1.4M" on screen meets "1.4"
 * in the transcript, and any other word case-folded.
 */
function wordKeys(text: string): string[] {
    const keys: string[] = [];
    for (const { segment, isWordLike } of WORDS.segment(text.normalize('NFKC'))) {
        if (!isWordLike) {
            continue;
        }
        const word = segment.toLocaleLowerCase();
        const figure = word.match(/\p{Nd}[\p{Nd},]*(?:\.\p{Nd}+)?/u);
        keys.push(figure ? `#${figure[0].replace(/,/g, '')}` : word);
    }
    return keys;
}

type NarrationWord = NonNullable<RuntimeLintContext['narration']>[number];

/**
 * When the voice starts saying `line`: the earliest spoken word it shares with the narration, or
 * null when too little of it is spoken for the narration to be what it says.
 */
function spokenOnset(
    line: string,
    onsets: ReadonlyMap<string, NarrationWord>,
): NarrationWord | null {
    const keys = [...new Set(wordKeys(line))];
    const spoken = keys.flatMap((key) => onsets.get(key) ?? []);
    if (keys.length === 0 || spoken.length / keys.length <= SPOKEN_SHARE) {
        return null;
    }
    return spoken.reduce((earliest, word) =>
        word.startSeconds < earliest.startSeconds ? word : earliest,
    );
}

/** Each word key the narration contains, mapped to the first spoken word carrying it. */
function narrationOnsets(narration: readonly NarrationWord[]): Map<string, NarrationWord> {
    const onsets = new Map<string, NarrationWord>();
    for (const word of narration) {
        for (const key of wordKeys(word.word)) {
            const known = onsets.get(key);
            if (!known || word.startSeconds < known.startSeconds) {
                onsets.set(key, word);
            }
        }
    }
    return onsets;
}

function trim(value: number): string {
    return value.toFixed(1).replace(/\.0$/, '');
}

function shownAtSeconds(entry: LeafVisibility, sceneSeconds: number): number {
    const firstVisible = entry.samples.find((sample) => sample.visible);
    return (firstVisible?.progress ?? 0) * sceneSeconds;
}

/** Whether the leaf spells whole words of its line, rather than letters of one of them. */
function spellsWordsOfLine(entry: LeafVisibility): boolean {
    const words = wordKeys(entry.settledText);
    const lineWords = new Set(wordKeys(entry.settledLine));
    return words.length > 0 && words.every((word) => lineWords.has(word));
}

/**
 * The legible single-word leaves of each line, in reading order: the shape a word-by-word reveal
 * is built in. A leaf holding a phrase is timed as a phrase, which the lead rule judges.
 */
function wordLeavesByLine(visibility: readonly LeafVisibility[]): Map<string, LeafVisibility[]> {
    const byLine = new Map<string, LeafVisibility[]>();
    for (const entry of visibility) {
        if (
            neverVisible(entry) ||
            wordKeys(entry.settledText).length !== 1 ||
            !spellsWordsOfLine(entry)
        ) {
            continue;
        }
        byLine.set(entry.settledLine, [...(byLine.get(entry.settledLine) ?? []), entry]);
    }
    return byLine;
}

/** Copy the voice hasn't reached yet, judged a line at a time from its earliest spoken word. */
function copyAheadOfVoice(
    visibility: readonly LeafVisibility[],
    onsets: ReadonlyMap<string, NarrationWord>,
    sceneSeconds: number,
    carriedIn: ReadonlySet<string> | undefined,
): string[] {
    const messages: string[] = [];
    const reportedLines = new Set<string>();
    for (const entry of visibility) {
        if (neverVisible(entry) || reportedLines.has(entry.settledLine)) {
            continue;
        }
        const anchor = spokenOnset(entry.settledLine, onsets);
        if (!anchor) {
            continue;
        }
        // Carried in from the graphic before: the viewer has been reading it since before the cut,
        // and bringing it in on the word would undo the seam.
        const carry = entry.leaf.element.closest('[data-l5-carry]');
        const ids = (carry?.getAttribute('data-l5-carry') ?? '').split(/\s+/);
        if (entry.samples[0]?.visible && ids.some((id) => carriedIn?.has(id))) {
            continue;
        }
        const shownAt = shownAtSeconds(entry, sceneSeconds);
        if (shownAt + LEAD_TOLERANCE_SECONDS >= anchor.startSeconds) {
            continue;
        }
        const spoken = anchor.word.replace(/\p{P}+$/u, '');
        reportedLines.add(entry.settledLine);
        const shown = spellsWordsOfLine(entry) ? entry.settledText : entry.settledLine;
        const copy = shown.length > 48 ? `${shown.slice(0, 45)}…` : shown;
        messages.push(
            `"${copy}" is on screen from ${trim(shownAt)}s, but the narration ` +
                `doesn't say "${spoken}" until ${trim(anchor.startSeconds)}s — the viewer reads ` +
                `it before hearing it. Bring it in on the word, and open the scene on what the ` +
                `voice is already saying.`,
        );
    }
    return messages;
}

/**
 * Lateness, judged only in the one shape where it is never a choice: a line revealed word by word.
 * A whole line landing as its sentence ends can be a verdict arriving on purpose, so it is left
 * alone; a line built word against word has promised to keep time with the voice, and a word that
 * turns up after it was said is the line trailing it — typically a fixed stagger standing in for the
 * cues.
 */
function wordsBehindVoice(
    visibility: readonly LeafVisibility[],
    narration: readonly NarrationWord[],
    onsets: ReadonlyMap<string, NarrationWord>,
    sceneSeconds: number,
): string[] {
    const messages: string[] = [];
    for (const [line, words] of wordLeavesByLine(visibility)) {
        const shown = words.map((entry) => shownAtSeconds(entry, sceneSeconds));
        if (words.length < MIN_REVEALED_WORDS || new Set(shown).size < 2) {
            continue;
        }
        if (!spokenOnset(line, onsets)) {
            continue;
        }
        // Each word is timed against the last time it was said before it appeared, so a word an
        // earlier sentence also used is not measured from that sentence. The cursor keeps a word the
        // line repeats on its own later occurrence; a word shown before any occurrence is early, which
        // `copyAheadOfVoice` judges.
        let cursor = 0;
        let worst: { word: string; shownAt: number; spokenAt: number; lag: number } | null = null;
        for (const [index, entry] of words.entries()) {
            const [key] = wordKeys(entry.settledText);
            let found = -1;
            for (let at = cursor; at < narration.length; at++) {
                if (narration[at].startSeconds > shown[index]) {
                    break;
                }
                if (wordKeys(narration[at].word).includes(key)) {
                    found = at;
                }
            }
            if (found === -1) {
                continue;
            }
            cursor = found + 1;
            const spokenAt = narration[found].startSeconds;
            const lag = shown[index] - spokenAt;
            if (!worst || lag > worst.lag) {
                worst = { word: entry.settledText.trim(), shownAt: shown[index], spokenAt, lag };
            }
        }
        if (!worst || worst.lag <= LAG_TOLERANCE_SECONDS) {
            continue;
        }
        const { word, shownAt, spokenAt } = worst;
        messages.push(
            `"${word}" is legible from ${trim(shownAt)}s, but the narration says it at ` +
                `${trim(spokenAt)}s — "${line}" trails the voice. Position each word's tween ` +
                `at its own [N.Ns] cue from the scene's cues line, not on a fixed stagger.`,
        );
    }
    return messages;
}

/**
 * Copy out of time with the voiceover, in either direction. One rule, because the agent reads every
 * rule's guidance on every design turn and both halves ask the same thing of it; the two checks stay
 * separate inside it because early and late are judged differently — early against a line's first
 * spoken word, late against each revealed word's own occurrence.
 */
export const textOutOfTimeWithNarration: RuntimeLintRule = {
    id: 'text-out-of-time-with-narration',
    severity: 'warning',
    guidance:
        'Keep copy in time with the voiceover: bring each piece in on the word it shares with the ' +
        'narration, not ahead of the voice, and bring each word of a line revealed word by word in ' +
        'on its own spoken onset, not on a fixed stagger that falls behind it.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const { narration, sceneDurationMs } = context;
        if (!narration?.length || !(typeof sceneDurationMs === 'number' && sceneDurationMs > 0)) {
            return [];
        }
        const sceneSeconds = sceneDurationMs / 1000;
        const onsets = narrationOnsets(narration);
        const visibility = await sampleTextVisibility(context);
        return [
            ...copyAheadOfVoice(visibility, onsets, sceneSeconds, context.carriedIn),
            ...wordsBehindVoice(visibility, narration, onsets, sceneSeconds),
        ];
    },
};
